import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { usePostUpsertMeasurement } from '../../../../../redux/api/users/usersApi';
import {
  DEFAULT_MEASUREMENT_UNIT_BY_PARAMETER,
  MEASUREMENT_UNIT_SELECTOR_ENABLED,
} from '../../../../../shared/constants/user-measurements';
import {
  IUserMeasurement,
  MeasurementParameter,
  MeasurementUnitKey,
} from '../../../../../types/interfaces/user-measurement';
import { getTodayMeasuredDate } from '../../../../../utils/measurements/measurement-utils';
import { getRecordMeasurementFormValidation } from './FormikData';

interface IUseRecordMeasurementModalParams {
  parameter: MeasurementParameter;
  latestMeasurement: IUserMeasurement | undefined;
  onSuccess: () => void;
}

export function useRecordMeasurementModal({
  parameter,
  latestMeasurement,
  onSuccess,
}: IUseRecordMeasurementModalParams) {
  const { t } = useTranslation();
  const [postUpsertMeasurement, { isLoading }] = usePostUpsertMeasurement();

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getRecordMeasurementFormValidation(t),
    initialValues: {
      value: latestMeasurement?.value ?? '',
      unitKey: MEASUREMENT_UNIT_SELECTOR_ENABLED
        ? (latestMeasurement?.unitKey ??
          DEFAULT_MEASUREMENT_UNIT_BY_PARAMETER[parameter])
        : DEFAULT_MEASUREMENT_UNIT_BY_PARAMETER[parameter],
    },
    onSubmit,
  });

  function onSubmit(values: {
    value: number | string;
    unitKey: MeasurementUnitKey;
  }) {
    return postUpsertMeasurement({
      parameter,
      measuredDate: getTodayMeasuredDate(),
      value: Number(values.value),
      unitKey: values.unitKey,
    })
      .unwrap()
      .then(() => {
        onSuccess();
      });
  }

  function handleUnitKeyChange(unitKey: MeasurementUnitKey) {
    formik.setFieldValue('unitKey', unitKey, true).then(() => {
      if (formik.touched.value) {
        formik.validateField('value');
      }
    });
  }

  return { formik, isLoading, handleUnitKeyChange };
}
